// UploadRSS.tsx
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from 'util/auth'
import { Pagination } from './Pagination'
import { UploadRssEpisodesList } from './UploadRssEpisodesList'
import { UploadRssInput } from './UploadRssInput'
import { Loader } from './Loader'
import AlertMessage from './AlertMessage'
import { UploadTask, useUploadStore } from 'hooks/useUploadStore'
import { v4 as uuidv4 } from 'uuid'
import { useRouter } from 'next/router'
import { useSetupStore } from 'hooks/useSetupStore'
import Sentry from 'util/sentry'

interface RSSItem {
  title: string
  url: string
}

export function UploadRSS({ onUpload }: { onUpload: (data: any) => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()
  const [rssFeedUrl, setRssFeedUrl] = useState<string>('')
  const [podcastData, setPodcastData] = useState<RSSItem[]>([])
  const [fetchingRss, setFetchingRss] = useState(false)
  const [rssError, setRssError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const auth = useAuth()
  const { addUploads, uploads } = useUploadStore()
  const router = useRouter()
  const project_id =
    router.query.project_id || useSetupStore.getState().project?.id

  const onSubmit = async () => {
    if (rssFeedUrl) {
      try {
        setFetchingRss(true)
        const response = await fetch(
          `/api/fetch-rss-feed?url=${encodeURIComponent(rssFeedUrl)}`
        )
        const feed = await response.json()
        const data = feed.items.map((item) => ({
          title: item.title,
          url: item.url,
          published: item.pubDate,
          image: item.image,
        }))
        setPodcastData(data)
        setFetchingRss(false)
      } catch (error) {
        Sentry.captureException(error)
        console.error('Error fetching RSS feed:', error)
        setRssError(
          'Failed to fetch the RSS feed. Please check the URL and try again.'
        )
        setFetchingRss(false)
      }
    }
  }

  const handleUpload = (episode) => {
    if (!episode) return

    const existingUpload = uploads.find(
      (upload) => upload.episodeUrl === episode.url && upload.type === 'rss'
    )

    if (existingUpload) {
      // Optionally inform the user that the episode is already being uploaded
      return
    }

    const uploadId = uuidv4()

    const newUpload: UploadTask = {
      id: uploadId,
      episodeUrl: episode.url,
      name: episode.title,
      progress: 0,
      status: 'pending',
      userId: auth.user ? auth.user.id : '',
      type: 'rss',
      projectId: parseInt(project_id as string) || undefined,
    }

    addUploads([newUpload])
    onUpload?.(newUpload)
  }

  // Pagination logic
  const endIndex = currentPage * itemsPerPage
  const startIndex = endIndex - itemsPerPage
  const displayedEpisodes = podcastData.slice(startIndex, endIndex)

  // Reset RSS error when a new URL is entered
  useEffect(() => {
    if (rssFeedUrl) {
      setRssError(null)
    }
  }, [rssFeedUrl])

  return (
    <div className="flex w-full flex-col rounded-lg pb-6">
      <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col">
        <div className="w-full">
          <UploadRssInput
            register={register}
            errors={errors}
            rssFeedUrl={rssFeedUrl}
            setRssFeedUrl={setRssFeedUrl}
          />
        </div>
      </form>
      <div className="mt-1 overflow-hidden rounded-md">
        {fetchingRss && (
          <Loader className="flex h-10 w-full justify-center text-center" />
        )}
        {!fetchingRss && rssError && (
          <div className="mt-4 text-sm">
            <AlertMessage message={rssError} type="warning" />
          </div>
        )}
        {!fetchingRss && displayedEpisodes.length > 0 && (
          <>
            <p className="mb-2 text-sm text-gray-500 underline underline-offset-4">
              Select an episode to import:
            </p>
            <UploadRssEpisodesList
              episodes={displayedEpisodes}
              handleUpload={handleUpload}
              uploads={uploads}
            />
          </>
        )}
        {podcastData.length > itemsPerPage && (
          <Pagination
            totalItems={podcastData.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </div>
    </div>
  )
}
